import type { ReactNode } from "react";
import { useTranslate } from "ra-core";

import { cn } from "@/lib/utils";

import { ProgressBar } from "../design/ProgressBar";
import { APP_LOCALE } from "../misc/locale";
import type { IncentiveProgressModel } from "./incentiveProgress";

/**
 * Almikia Epic E7 (TASK-092): one card per applicable, eligible incentive
 * program on "Mi avance" -- `best_bpo_team` is filtered out here even if a
 * caller ever passes it (it lives on the Líder BPO dashboard, TASK-093,
 * never on the advisor's own screen). Program names and parameters are read
 * from `IncentiveProgressModel` (itself derived from `incentive_programs`
 * DATA), never hardcoded here beyond the copy strings.
 */

type Translate = ReturnType<typeof useTranslate>;

/** MXN currency formatter shared with `MyProgressPage`'s own-goal figures. */
const MXN_FORMAT = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 0,
});

/** Formats a MXN amount for display (e.g. "$1,250"), never an invented precision. */
export function formatMxn(amountMxn: number): string {
  return MXN_FORMAT.format(amountMxn);
}

/**
 * Stable render order (never a ranking, never re-sorted by value): the
 * program the advisor most immediately acts on first. `best_bpo_team` is
 * deliberately absent -- it never renders on this screen.
 */
const PROGRAM_ORDER: IncentiveProgressModel["code"][] = [
  "monthly_sale_incentive",
  "quarterly_outstanding_sales",
  "monthly_proactive_appointments",
  "gasoline_insurance",
];

function sortModels(
  models: IncentiveProgressModel[],
): IncentiveProgressModel[] {
  return [...models]
    .filter((model) => model.code !== "best_bpo_team")
    .sort(
      (a, b) => PROGRAM_ORDER.indexOf(a.code) - PROGRAM_ORDER.indexOf(b.code),
    );
}

const CardShell = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    data-testid="incentive-bar"
    className={cn("rounded-2xl border bg-card p-4", className)}
  >
    {children}
  </div>
);

function MonthlySaleIncentiveCard({
  model,
  translate,
}: {
  model: Extract<IncentiveProgressModel, { code: "monthly_sale_incentive" }>;
  translate: Translate;
}) {
  return (
    <CardShell>
      <p className="text-base font-bold">
        {translate("crm.my_progress.program.monthly_sale_incentive.title")}
      </p>
      <p className="mt-1 text-2xl font-bold tabular-nums">
        {formatMxn(model.estimatedMxn)}
      </p>
      <p className="text-base tabular-nums text-muted-foreground">
        {translate(
          "crm.my_progress.program.monthly_sale_incentive.sales_count",
          { smart_count: model.salesCount },
        )}
      </p>
    </CardShell>
  );
}

/**
 * Bar progress toward a quantity (design-system section 8): before
 * `min_units` is met, the bar tracks units (the unit floor DOMAIN RULE (f)
 * gates on first); once met, it tracks MXN toward the next tier's own
 * threshold. Once the top tier is already reached (`nextTierThresholdMxn:
 * null`), the bar renders full -- there is no further quantity to progress
 * toward, but the level itself stays a completed state, never an empty one.
 */
function quarterlyBarProps(
  model: Extract<
    IncentiveProgressModel,
    { code: "quarterly_outstanding_sales" }
  >,
): { value: number; max: number } {
  if (model.units < model.minUnits) {
    return { value: model.units, max: model.minUnits };
  }
  if (model.nextTierThresholdMxn != null) {
    return { value: model.amountMxn, max: model.nextTierThresholdMxn };
  }
  return { value: 1, max: 1 };
}

function QuarterlyOutstandingSalesCard({
  model,
  translate,
}: {
  model: Extract<
    IncentiveProgressModel,
    { code: "quarterly_outstanding_sales" }
  >;
  translate: Translate;
}) {
  const { value, max } = quarterlyBarProps(model);
  return (
    <CardShell>
      <p className="text-base font-bold">
        {translate("crm.my_progress.program.quarterly_outstanding_sales.title")}
      </p>
      {model.tierReachedRatePct != null ? (
        <p className="mt-1 text-base tabular-nums">
          {translate(
            "crm.my_progress.program.quarterly_outstanding_sales.tier_reached",
            { rate: model.tierReachedRatePct },
          )}
        </p>
      ) : (
        <p className="mt-1 text-base tabular-nums">
          {translate(
            "crm.my_progress.program.quarterly_outstanding_sales.missing",
            {
              units: model.unitsMissingToNextTier,
              amount: formatMxn(model.mxnMissingToNextTier),
            },
          )}
        </p>
      )}
      <ProgressBar
        value={value}
        max={max}
        label={translate(
          "crm.my_progress.program.quarterly_outstanding_sales.bar_label",
        )}
        className="mt-2"
      />
    </CardShell>
  );
}

function MonthlyProactiveAppointmentsCard({
  model,
  translate,
}: {
  model: Extract<
    IncentiveProgressModel,
    { code: "monthly_proactive_appointments" }
  >;
  translate: Translate;
}) {
  return (
    <CardShell>
      <p className="text-base font-bold">
        {translate(
          "crm.my_progress.program.monthly_proactive_appointments.title",
        )}
      </p>
      <p className="mt-1 text-base tabular-nums">
        {translate(
          "crm.my_progress.program.monthly_proactive_appointments.count",
          { count: model.count, target: model.target },
        )}
      </p>
      <ProgressBar
        segments={model.target}
        value={model.count}
        label={translate(
          "crm.my_progress.program.monthly_proactive_appointments.bar_label",
        )}
        className="mt-2"
      />
    </CardShell>
  );
}

function GasolineInsuranceCard({ translate }: { translate: Translate }) {
  return (
    <CardShell className="text-center">
      <p className="text-base font-bold">
        {translate("crm.my_progress.program.gasoline_insurance.title")}
      </p>
      {/* DOMAIN RULE (i): designed empty state, never an invented count. */}
      <p className="mt-1 text-base text-muted-foreground">
        {translate("crm.my_progress.program.gasoline_insurance.empty")}
      </p>
    </CardShell>
  );
}

export interface IncentiveBarsProps {
  models: IncentiveProgressModel[];
}

/**
 * Exactly one card per applicable, eligible program the advisor is offered
 * (acceptance criterion), in `PROGRAM_ORDER`. A program absent from `models`
 * (not applicable, or the advisor not eligible) simply renders nothing --
 * `computeAdvisorProgramProgress` already excludes it, this component never
 * invents a placeholder for it.
 */
export function IncentiveBars({ models }: IncentiveBarsProps) {
  const translate = useTranslate();
  const sorted = sortModels(models);

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((model) => {
        switch (model.code) {
          case "monthly_sale_incentive":
            return (
              <MonthlySaleIncentiveCard
                key={model.code}
                model={model}
                translate={translate}
              />
            );
          case "quarterly_outstanding_sales":
            return (
              <QuarterlyOutstandingSalesCard
                key={model.code}
                model={model}
                translate={translate}
              />
            );
          case "monthly_proactive_appointments":
            return (
              <MonthlyProactiveAppointmentsCard
                key={model.code}
                model={model}
                translate={translate}
              />
            );
          case "gasoline_insurance":
            return (
              <GasolineInsuranceCard key={model.code} translate={translate} />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
